// The controller's tuning from the tuning table (content/tables/tuning.json, group "controller"): every
// `controller.<key>` row is a ControllerTuning value; `mount.<key>` rows override the hero's for the stub mount;
// `controller.surface.<tag>.speed` rows are the surface rules. A missing row fails loudly: the table is the source.
import type { ControllerTuning, SurfaceRule } from "@rpg/controller";

/** What the valley reads from the tuning table: a row's value by id. */
export type TuningLookup = (id: string) => number | undefined;

const KEYS: readonly (keyof ControllerTuning)[] = [
  "radius", "halfHeight", "skin", "walkSpeed", "runSpeed", "sprintSpeed", "deadZone", "walkTilt", "runTilt", "accelTime", "stopDistance", "turnTime",
  "skidAngle", "skidDecel", "pivotSpeed", "pivotAngle", "pivotRelease", "pivotRate", "burstTime", "burstBoost", "burstAccel", "maxSlope", "stepHeight",
  "stepMinWidth", "snapDistance", "jumpHeight", "jumpRiseTime", "fallGravityScale", "jumpCutGravityScale", "maxFallSpeed", "coyoteTime", "jumpBuffer",
  "airAccel", "airTurnRate", "airDrag", "glideSinkSpeed", "glideSpeed", "glideIdleSpeed", "glideAccel", "glideTurnRate", "glideBrake", "glideMinHeight",
  "wadeSpeedScale", "wadeDepth", "swimDepth",
];

/** The valley's tuning: the hero's, the stub mount's, the surfaces and the mount's seat and reach. */
export interface ValleyTuning {
  hero: ControllerTuning;
  mount: ControllerTuning;
  surfaces: Record<string, SurfaceRule>;
  seatHeight: number;
  reach: number;
}

/** Read the valley's tuning. Throws naming every missing `controller.*` row. */
export function readTuning(get: TuningLookup, rowIds: readonly string[]): ValleyTuning {
  const missing: string[] = [];
  const need = (id: string): number => {
    const v = get(id);
    if (v === undefined) missing.push(id);
    return v ?? Number.NaN;
  };
  const hero = Object.fromEntries(KEYS.map((k) => [k, need(`controller.${k}`)])) as unknown as ControllerTuning;
  const mount = { ...hero };
  for (const k of KEYS) {
    const v = get(`mount.${k}`);
    if (v !== undefined) mount[k] = v;
  }
  const surfaces: Record<string, SurfaceRule> = {};
  for (const id of rowIds) {
    const m = /^controller\.surface\.([^.]+)\.speed$/.exec(id);
    if (m) surfaces[m[1]!] = { speed: need(id) };
  }
  const seatHeight = need("mount.seatHeight");
  const reach = need("mount.reach");
  if (missing.length) throw new Error(`valley tuning: missing rows ${missing.join(", ")}`);
  return { hero, mount, surfaces, seatHeight, reach };
}
